import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";
import { PhotoCard } from "@/components/PhotoCard";
import { useMall } from "@/context/MallContext";
import { useShop } from "@/lib/useShop";
import { PickShopGate } from "@/components/mall/PickShopGate";
import { cardTheme, receded, sortStoreFirst } from "@/lib/mall";
import { Crumbs, readyPills } from "@/components/layout/Crumbs";
import { READY_BY_KEY, READY_IN } from "@/lib/nav";

export default function SignCategory() {
    const { category } = useParams();
    const [params] = useSearchParams();
    const ready = READY_BY_KEY[params.get("ready")] ? params.get("ready") : null;
    const { slug: activeSlug, store: activeStore } = useMall();
    const { shopSlug } = useShop();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!shopSlug) return;
        setLoading(true);
        api.get(`/signs/categories/${category}`, { params: { store: shopSlug, ready: ready || undefined } })
            .then(({ data }) => setData(data)).catch(() => { }).finally(() => setLoading(false));
    }, [category, shopSlug, ready]);

    if (!shopSlug) return <Layout><PickShopGate room="signs" title="Which shop makes your sign?" to={`/signs/${category}`} /></Layout>;
    if (loading) return <Layout><div className="flex min-h-[60vh] items-center justify-center text-zinc-400"><Loader2 className="animate-spin" /></div></Layout>;

    if (!data) {
        return (
            <Layout>
                <div className="mx-auto max-w-3xl px-4 py-24 text-center">
                    <h1 className="font-display text-3xl font-black">Category not found</h1>
                    <Link to="/signs" className="mt-4 inline-block underline">All materials</Link>
                </div>
            </Layout>
        );
    }

    const products = sortStoreFirst(data.products, activeSlug);
    const mixed = products.some((p) => p.store?.slug !== products[0]?.store?.slug);
    return (
        <Layout>
            <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
                <Reveal>
                    <Crumbs trail={[{ label: "Signs", to: "/signs" }, ...(ready ? [{ label: READY_BY_KEY[ready].label, to: `/signs?ready=${ready}` }] : []), { label: data.category.name }]}
                        pills={readyPills(`/signs/${category}`, {}, ready, READY_IN).map((x) => ({ ...x, count: undefined }))} />
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                        <Link to="/signs" className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-3 py-1.5 text-xs font-semibold text-zinc-700 transition-colors hover:border-zinc-400" data-testid="back-to-materials">
                            <ArrowLeft size={13} /> All materials
                        </Link>
                        <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl" data-testid="sign-category-title">{data.category.name}</h1>
                        <p className="text-sm text-zinc-500" data-testid="sign-category-store-note">
                            {products.length} products
                            {activeStore ? <> · made by <span className="font-semibold text-zinc-900">{activeStore.name}</span></> : " · each one made by the shop on the card"}
                            <span className="hidden sm:inline"> · hover a card for details</span>
                        </p>
                    </div>
                </Reveal>

                <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="sign-products">
                    {products.map((p, i) => (
                        <Reveal key={p.slug} delay={i * 0.05}>
                            <PhotoCard to={`/signs/${data.category.slug}/${p.slug}`} image={p.photo_override || p.image_url} name={p.name} detail={p.short_description} turnaround={p.ready_label}
                                theme={cardTheme(p, activeStore)} store={p.store} mine={Boolean(activeSlug && p.store?.slug === activeSlug)}
                                tag={mixed && activeSlug && p.store?.slug === activeSlug} mixed={mixed} receded={receded(p, activeSlug)}
                                cta="Build & order" testid={`sign-product-${p.slug}`} />
                        </Reveal>
                    ))}
                </div>
            </section>
        </Layout>
    );
}
